import { http, type Transport, type Chain } from "viem";

/**
 * Global rate limiter using token bucket algorithm
 */
class RateLimiter {
  private tokens: number;
  private lastRefill: number;
  private readonly maxTokens: number;
  private readonly refillRate: number; // tokens per second

  constructor(maxTokens: number = 5, refillRate: number = 50) {
    this.maxTokens = maxTokens;
    this.refillRate = refillRate;
    this.tokens = maxTokens;
    this.lastRefill = Date.now();
  }

  private refillTokens(): void {
    const now = Date.now();
    const timePassed = (now - this.lastRefill) / 1000; // seconds
    const tokensToAdd = timePassed * this.refillRate;

    this.tokens = Math.min(this.maxTokens, this.tokens + tokensToAdd);
    this.lastRefill = now;
  }

  async waitForToken(): Promise<void> {
    this.refillTokens();

    if (this.tokens >= 1) {
      this.tokens -= 1;
      return;
    }

    // Need to wait for tokens to refill
    const waitTime = (1 - this.tokens) / this.refillRate * 1000; // ms
    console.log(`⏳ Rate limit: waiting ${Math.round(waitTime)}ms for next request`);

    await new Promise(resolve => setTimeout(resolve, waitTime));
    this.tokens = 0; // Consume the token we waited for
  }

  getStatus() {
    this.refillTokens();
    return {
      tokens: Math.floor(this.tokens),
      maxTokens: this.maxTokens,
      refillRate: this.refillRate
    };
  }
}

// Single global rate limiter instance shared across all transports
export const globalRateLimiter = new RateLimiter(5, 50);

/**
 * Custom transport that wraps HTTP transport with rate limiting
 */
export const createRateLimitedTransport = (
  url?: string,
  options: {
    maxTokens?: number;
    refillRate?: number;
    batch?: { batchSize?: number; wait?: number };
    bypassForNFTMetadata?: boolean;
  } = {}
): Transport => {
  const baseTransport = http(url, {
    batch: {
      batchSize: options.batch?.batchSize || 25,
      wait: options.batch?.wait || 300,
    },
    retryCount: 3,
    retryDelay: 500,
  });

  return (params) => {
    const transport = baseTransport(params);
    const originalRequest = transport.request;

    transport.request = async (requestParams: any) => {
      // Check if this is an IPFS or metadata request that should bypass rate limiting
      const isNFTMetadataRequest = options.bypassForNFTMetadata ||
        (typeof requestParams === 'object' && requestParams?.url && (
          requestParams.url.includes('ipfs.io') ||
          requestParams.url.includes('ipfs://') ||
          requestParams.url.includes('.json') ||
          requestParams.url.includes('myfilebase.com') ||
          requestParams.url.includes('pinata.cloud') ||
          requestParams.url.includes('cloudflare-ipfs.com')
        ));

      // Only apply rate limiting to actual blockchain RPC calls
      if (!isNFTMetadataRequest) {
        await globalRateLimiter.waitForToken(); // Use global rate limiter

        const status = globalRateLimiter.getStatus();
        console.log(`🌐 Global RPC request (${status.tokens}/${status.maxTokens} tokens)`);

        // Track request for monitoring (if available)
        if (typeof window !== 'undefined' && (window as any).rateLimitStats) {
          (window as any).rateLimitStats.incrementTotal();
        }
      } else {
        console.log(`🖼️ NFT metadata request bypassing rate limit: ${requestParams?.url || 'unknown'}`);
      }

      try {
        return await originalRequest(requestParams);
      } catch (error) {
        // Track errors for monitoring (only for RPC calls)
        if (!isNFTMetadataRequest && typeof window !== 'undefined' && (window as any).rateLimitStats) {
          (window as any).rateLimitStats.incrementErrors();
        }

        // If it's a rate limit error, add extra delay
        if (error instanceof Error && error.message.includes('rate limit')) {
          console.warn('⚠️ Rate limit hit, adding extra delay...');
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        throw error;
      }
    };

    return transport;
  };
};

/**
 * Create a public client with global rate limiting
 */
export const createRateLimitedPublicClient = (
  chain: Chain,
  rpcUrl?: string,
  rateOptions?: {
    maxTokens?: number;
    refillRate?: number;
    batchSize?: number;
    wait?: number;
  }
) => {
  return import('viem').then(({ createPublicClient }) =>
    createPublicClient({
      chain,
      transport: createRateLimitedTransport(rpcUrl, {
        maxTokens: rateOptions?.maxTokens || 5,
        refillRate: rateOptions?.refillRate || 2,
        batch: {
          batchSize: rateOptions?.batchSize || 40,
          wait: rateOptions?.wait || 150,
        },
      }),
    })
  );
};

/**
 * Global transport factory with shared rate limiter
 * Use this to ensure ALL clients share the same rate limit
 */
export const createGlobalRateLimitedTransport = (url?: string) => {
  const baseTransport = http(url, {
    batch: { batchSize: 45, wait: 150 },
    retryCount: 3,
    retryDelay: 500,
  });

  return (params: any) => {
    const transport = baseTransport(params);
    const originalRequest = transport.request;

    transport.request = async (requestParams: any) => {
      await globalRateLimiter.waitForToken();

      const status = globalRateLimiter.getStatus();
      console.log(`🌐 Global RPC request (${status.tokens}/${status.maxTokens} tokens)`);

      return originalRequest(requestParams);
    };

    return transport;
  };
};

// Rate limiting configurations
export const RATE_LIMIT_CONFIGS = {
  STRICT: { maxTokens: 3, refillRate: 10 },    // 10 req/sec max, 3 burst
  MODERATE: { maxTokens: 5, refillRate: 25 },  // 25 req/sec max, 5 burst
  GLOBAL: { maxTokens: 5, refillRate: 50 },    // 50 req/sec max, 5 burst (default)
  RELAXED: { maxTokens: 10, refillRate: 100 }, // 100 req/sec max, 10 burst
} as const;